/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  ArrowBack,
  Edit,
  Delete,
  Image as ImageIcon,
} from "@mui/icons-material";
import { getProductById, deleteProduct } from "actions";
import { clearError, clearCurrentProduct } from "features/productsSlice";
import { PATHNAME } from "constants/pathnames";
import { ADMIN_TEXTS } from "constants/adminTexts";
import { FILE_TYPE } from "constants/fileTypes";
import { BrandButton } from "components/brand";

const imageBase = process.env.REACT_APP_API_IMAGE_URL;

const ProductDetail = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    id: null,
    title: "",
  });

  const { currentProduct, loading, error } = useSelector(
    (state) => state.products,
  );

  const handleBack = () => {
    navigate(PATHNAME.products);
  };

  const handleEdit = () => {
    navigate(`/products/${id}/edit`);
  };

  const handleDelete = () => {
    dispatch(deleteProduct(deleteDialog.id));
    setDeleteDialog({ open: false, id: null, title: "" });
    navigate(PATHNAME.products);
  };

  const handleDeleteClick = () => {
    setDeleteDialog({
      open: true,
      id: currentProduct.id,
      title: currentProduct.title || ADMIN_TEXTS.products.noData,
    });
  };

  const getImageUrl = (image) => {
    if (!image) return null;
    return `${imageBase}${image.path}`;
  };

  useEffect(() => {
    if (id) {
      dispatch(getProductById(id));
    }
    return () => {
      dispatch(clearCurrentProduct());
    };
  }, [id]);

  useEffect(() => {
    if (error) {
      dispatch(clearError());
    }
  }, [error]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <BrandButton variant="secondary" onClick={handleBack}>
          {ADMIN_TEXTS.products.backToProducts}
        </BrandButton>
      </Box>
    );
  }

  if (!currentProduct) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="text.secondary">
          {ADMIN_TEXTS.products.productNotFound}
        </Typography>
        <BrandButton variant="secondary" onClick={handleBack} sx={{ mt: 2 }}>
          {ADMIN_TEXTS.products.backToProducts}
        </BrandButton>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={handleBack} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" component="h1" fontWeight="bold">
          {ADMIN_TEXTS.products.details}
        </Typography>
      </Box>

      <Box
        display="flex"
        gap={3}
        sx={{ flexDirection: { xs: "column", md: "row" } }}
      >
        <Box flex={1}>
          <Card>
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="flex-start"
                mb={3}
              >
                <Box>
                  <Typography variant="h5" component="h2" gutterBottom>
                    {currentProduct.title}
                  </Typography>
                  <Box display="flex" gap={1} mb={2} flexWrap="wrap">
                    <Chip
                      label={`${ADMIN_TEXTS.common.order}: ${currentProduct.order}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                    <Chip
                      label={
                        currentProduct.active
                          ? ADMIN_TEXTS.common.active
                          : ADMIN_TEXTS.common.inactive
                      }
                      size="small"
                      color={currentProduct.active ? "success" : "default"}
                      variant="outlined"
                    />
                    <Chip
                      label={
                        currentProduct.isMenuSliderItem
                          ? ADMIN_TEXTS.menuSlider.inMenuSlider
                          : ADMIN_TEXTS.menuSlider.notInMenuSlider
                      }
                      size="small"
                      color={
                        currentProduct.isMenuSliderItem
                          ? "secondary"
                          : "default"
                      }
                      variant="outlined"
                    />
                  </Box>
                </Box>
                <Box display="flex" gap={1}>
                  <BrandButton
                    variant="secondary"
                    startIcon={<Edit />}
                    onClick={handleEdit}
                  >
                    {ADMIN_TEXTS.common.edit}
                  </BrandButton>
                  <BrandButton
                    variant="error"
                    startIcon={<Delete />}
                    onClick={handleDeleteClick}
                  >
                    {ADMIN_TEXTS.common.delete}
                  </BrandButton>
                </Box>
              </Box>

              <Divider sx={{ mb: 3 }} />

              <Box mb={3}>
                <Typography variant="h6" gutterBottom>
                  {ADMIN_TEXTS.common.description}
                </Typography>
                <Box
                  sx={{
                    "& p": { margin: 0, marginBottom: 1 },
                    "& ul, & ol": { margin: 0, paddingLeft: 2 },
                    "& h1, & h2, & h3": { margin: 0, marginBottom: 1 },
                    "& a": {
                      color: "primary.main",
                      textDecoration: "underline",
                    },
                  }}
                  dangerouslySetInnerHTML={{
                    __html: currentProduct.description,
                  }}
                />
              </Box>

              {currentProduct.price != null && (
                <Box mb={3}>
                  <Typography variant="h6" gutterBottom>
                    {ADMIN_TEXTS.products.price}
                  </Typography>
                  <Typography variant="body1">
                    ${currentProduct.price}
                  </Typography>
                </Box>
              )}

              <Box>
                <Typography variant="h6" gutterBottom>
                  {ADMIN_TEXTS.products.information}
                </Typography>
                <Paper sx={{ p: 2, backgroundColor: "grey.50" }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>{ADMIN_TEXTS.products.created}:</strong>{" "}
                    {new Date(currentProduct.createdAt).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>{ADMIN_TEXTS.products.updated}:</strong>{" "}
                    {new Date(currentProduct.updatedAt).toLocaleString()}
                  </Typography>
                </Paper>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {currentProduct.image && (
          <Box sx={{ width: { xs: "100%", md: 400 } }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {ADMIN_TEXTS.products.productImage}
                </Typography>
                <Box
                  sx={{
                    width: "100%",
                    height: 300,
                    borderRadius: 1,
                    overflow: "hidden",
                    position: "relative",
                    backgroundColor: "grey.100",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {currentProduct?.image?.type === FILE_TYPE.VIDEO ? (
                    <video
                      src={getImageUrl(currentProduct.image)}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                      controls
                    />
                  ) : (
                    <img
                      src={getImageUrl(currentProduct.image)}
                      alt="Product"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  )}
                </Box>
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    backgroundColor: "grey.50",
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    <strong>{ADMIN_TEXTS.products.file}:</strong>{" "}
                    {currentProduct.image.originalName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>{ADMIN_TEXTS.products.size}:</strong>{" "}
                    {Math.round(currentProduct.image.size / 1024)} KB
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>{ADMIN_TEXTS.products.type}:</strong>{" "}
                    {currentProduct.image.mimetype}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Box>
        )}
      </Box>

      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, id: null, title: "" })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Product</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{deleteDialog.title}"? This action
            cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <BrandButton
            variant="secondary"
            onClick={() =>
              setDeleteDialog({ open: false, id: null, title: "" })
            }
          >
            Cancel
          </BrandButton>
          <BrandButton variant="error" onClick={handleDelete}>
            Delete
          </BrandButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductDetail;
